import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { safeNextPath } from "../../const/auth";
import LoginForm from "./LoginForm";
import RegisterForm from "./RegisterForm";

type Mode = "login" | "register";

interface OpenOptions {
  /** Where to go after signing in; by default the user stays on the current page. */
  next?: string;
  /** Called when the modal is closed without signing in. */
  onDismiss?: () => void;
}

interface AuthModalApi {
  openLogin: (options?: OpenOptions) => void;
  openRegister: (options?: OpenOptions) => void;
}

// Without a provider (accounts disabled) opening the modal is a no-op.
const AuthModalContext = createContext<AuthModalApi>({ openLogin: () => {}, openRegister: () => {} });

export const useAuthModal = () => useContext(AuthModalContext);

export const AuthModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isLoggedIn: boolean = useSelector((state: any) => state.user.isLoggedIn);
  const [mode, setMode] = useState<Mode | null>(null);
  const options = useRef<OpenOptions>({});

  const open = useCallback((m: Mode, o: OpenOptions = {}) => {
    options.current = o;
    setMode(m);
  }, []);
  const api = useMemo<AuthModalApi>(() => ({
    openLogin: o => open("login", o),
    openRegister: o => open("register", o),
  }), [open]);

  const signedIn = () => {
    const { next } = options.current;
    options.current = {};
    setMode(null);
    if (next) navigate(safeNextPath(next), { replace: true });
  };

  const dismiss = () => {
    const { onDismiss } = options.current;
    options.current = {};
    setMode(null);
    onDismiss?.();
  };

  // Signing in from another tab makes an open modal pointless.
  useEffect(() => {
    if (isLoggedIn && mode) signedIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return (
    <AuthModalContext.Provider value={api}>
      {children}
      <Dialog open={mode !== null} onOpenChange={o => { if (!o) dismiss(); }}>
        <DialogContent className="max-w-sm w-[calc(100%-2rem)] rounded-2xl sm:rounded-2xl bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-800 p-6 max-h-[calc(100vh-2rem)] overflow-y-auto">
          <div className="text-center">
            <DialogTitle className="text-xl font-black text-gray-900 dark:text-gray-50">
              {mode === "register" ? t("auth.register") : t("auth.login")}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {mode === "register" ? t("auth.registerSubtitle") : t("auth.login")}
            </DialogDescription>
          </div>
          {mode === "register" ? (
            <RegisterForm onSignedIn={signedIn} onLogin={() => setMode("login")} />
          ) : (
            <LoginForm
              onSignedIn={signedIn}
              onRegister={() => setMode("register")}
              onForgot={() => { options.current = {}; setMode(null); navigate("/forgot"); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </AuthModalContext.Provider>
  );
};

/**
 * `/login` and `/register` stay valid URLs (emails, bookmarks, old links): they show the
 * home page with the modal open, and closing it leaves the user on the home page.
 */
export const AuthRoute: React.FC<{ mode: Mode; children: React.ReactNode }> = ({ mode, children }) => {
  const { openLogin, openRegister } = useAuthModal();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  useEffect(() => {
    const leave = () => navigate("/", { replace: true });
    (mode === "register" ? openRegister : openLogin)({ next: safeNextPath(next), onDismiss: leave });
  }, [mode, next, navigate, openLogin, openRegister]);
  return <>{children}</>;
};
